import { useQuery } from '@tanstack/react-query';
import { getSupabaseClient } from '../utils/supabaseClient';
import { getDisplayName } from '../utils/formatters';
import AdjustmentsReportingService from '../services/AdjustmentsReportingService';
import { parseDbDate, toDbDate } from '../utils/dateUtils';
import { companyWallClockToUtc } from '../utils/dateFormatters';

// A report day is the COMPANY's day. created_at / received_at are timestamptz, and a bare
// 'YYYY-MM-DD' bound is read as UTC midnight, so evening work in New York landed on the next
// day — and on month ends, the next month (a $1,498.58 payment at 10:34pm ET on June 30
// reported under July; audit 2026-10-17). Date columns (issue_date, expense_date, date) need
// none of this and keep their plain bounds.
const nextDay = (ymd) => {
  const [y, m, d] = String(ymd).split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + 1));
  return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`;
};
export const companyDayBounds = (dateRange) => ({
  from: companyWallClockToUtc(dateRange.start, '00:00') || dateRange.start,
  to: companyWallClockToUtc(nextDay(dateRange.end), '00:00') || `${dateRange.end}T23:59:59`,
});

// Regular/overtime split of one employee_timesheets row. ONE definition, shared by the
// timesheet totals below and the labour-cost function in Reports.js, so hours and the
// cost of those hours can never be split two different ways.
export const timesheetHoursSplit = (timesheet) => {
  const totalHours = Number(timesheet.total_hours || 0);
  return {
    totalHours,
    regularHours: Number(timesheet.regular_hours || Math.min(totalHours, 8)),
    overtimeHours: Number(timesheet.overtime_hours || Math.max(0, totalHours - 8))
  };
};

// Fetch timesheet reports
const fetchTimesheetReports = async ({ companyId, filters, userRole, userId }) => {
  if (!companyId) return { data: [], totals: null };
  const supabase = getSupabaseClient();

  let query = supabase
    .from('employee_timesheets')
    .select('*')
    .eq('company_id', companyId);

  // Apply role-based filtering
  if (userRole === 'EMPLOYEE') {
    query = query.eq('user_id', userId);
  }

  // Apply filters
  if (filters.employee !== 'all') {
    query = query.eq('employee_id', filters.employee);
  }

  if (filters.status !== 'all') {
    query = query.eq('status', filters.status);
  }

  if (filters.startDate) {
    query = query.gte('date', filters.startDate);
  }

  if (filters.endDate) {
    query = query.lte('date', filters.endDate);
  }

  query = query.order('date', { ascending: false }).order('employee_id', { ascending: true });

  const { data, error } = await query;

  if (error) {
    console.error('Error loading timesheet reports:', error);
    return { data: [], totals: null };
  }

  // Fetch employees to get names
  const { data: employees } = await supabase
    .from('employees')
    .select('id, first_name, last_name')
    .eq('company_id', companyId);
  
  const employeeMap = new Map((employees || []).map(e => [e.id, `${e.first_name || ''} ${e.last_name || ''}`.trim()]));

  // Fetch work orders to get job title and customer info
  const workOrderIds = [...new Set((data || []).map(t => t.work_order_id).filter(Boolean))];
  let workOrdersMap = {};
  if (workOrderIds.length > 0) {
    const { data: workOrders } = await supabase
      .from('work_orders')
      .select('id, title, customer_id')
      .in('id', workOrderIds);
    
    if (workOrders && workOrders.length > 0) {
      // Fetch customers
      const customerIds = [...new Set(workOrders.map(wo => wo.customer_id).filter(Boolean))];
      let customersMap = {};
      if (customerIds.length > 0) {
        const { data: customers } = await supabase
          .from('customers')
          .select('id, name')
          .in('id', customerIds);
        customersMap = (customers || []).reduce((acc, c) => { acc[c.id] = c; return acc; }, {});
      }
      
      workOrdersMap = (workOrders || []).reduce((acc, wo) => {
        acc[wo.id] = {
          title: wo.title,
          customer: customersMap[wo.customer_id] || null
        };
        return acc;
      }, {});
    }
  }

  // Add employee_name and work_order details to each timesheet record
  const timesheetsWithDetails = (data || []).map(timesheet => {
    const workOrder = timesheet.work_order_id ? workOrdersMap[timesheet.work_order_id] : null;
    return {
      ...timesheet,
      employee_name: employeeMap.get(timesheet.employee_id) || 'Unknown',
      // break_duration is already the correct column name (not break_minutes)
      title: workOrder?.title || null,
      customer: workOrder?.customer || null
    };
  });

  // Calculate totals - compute week/month in JS (not DB columns)
  const totals = {
    totalHours: 0,
    regularHours: 0,
    overtimeHours: 0,
    weeklyTotals: {},
    monthlyTotals: {},
    employeeTotals: {}
  };

  (timesheetsWithDetails || []).forEach(timesheet => {
    const { totalHours, regularHours, overtimeHours } = timesheetHoursSplit(timesheet);
    const employeeName = timesheet.employee_name;

    totals.totalHours += totalHours;
    totals.regularHours += regularHours;
    totals.overtimeHours += overtimeHours;

    // Compute week_start and month_start in JS
    // employee_timesheets.date is a DATE column: parse it as a LOCAL date (parseDbDate) and key
    // it back with toDbDate. `new Date('2026-10-04')` is UTC midnight, i.e. the previous day west
    // of UTC — a Sunday row fell into the prior week and 10/1 into September. This is the copy
    // the page renders on first load; Reports.js calculateTotals (fixed in 224a4a08b) only runs
    // after a filter change, which is why #837 came back (board #837, 2026-10-07).
    const timesheetDate = parseDbDate(timesheet.date);
    const weekStart = new Date(timesheetDate);
    weekStart.setDate(timesheetDate.getDate() - timesheetDate.getDay());
    const weekKey = toDbDate(weekStart);
    
    const monthKey = `${timesheetDate.getFullYear()}-${String(timesheetDate.getMonth() + 1).padStart(2, '0')}`;

    if (!totals.weeklyTotals[weekKey]) {
      totals.weeklyTotals[weekKey] = { totalHours: 0, regularHours: 0, overtimeHours: 0 };
    }
    totals.weeklyTotals[weekKey].totalHours += totalHours;
    totals.weeklyTotals[weekKey].regularHours += regularHours;
    totals.weeklyTotals[weekKey].overtimeHours += overtimeHours;

    if (!totals.monthlyTotals[monthKey]) {
      totals.monthlyTotals[monthKey] = { totalHours: 0, regularHours: 0, overtimeHours: 0 };
    }
    totals.monthlyTotals[monthKey].totalHours += totalHours;
    totals.monthlyTotals[monthKey].regularHours += regularHours;
    totals.monthlyTotals[monthKey].overtimeHours += overtimeHours;

    const employeeKey = timesheet.employee_id;
    if (!totals.employeeTotals[employeeKey]) {
      totals.employeeTotals[employeeKey] = {
        name: employeeName,
        totalHours: 0,
        regularHours: 0,
        overtimeHours: 0
      };
    }
    totals.employeeTotals[employeeKey].totalHours += totalHours;
    totals.employeeTotals[employeeKey].regularHours += regularHours;
    totals.employeeTotals[employeeKey].overtimeHours += overtimeHours;
  });

  return { data: timesheetsWithDetails, totals };
};

// Fetch employees
const fetchEmployees = async ({ companyId, userRole, userId }) => {
  if (!companyId) return [];
  const supabase = getSupabaseClient();

  let query = supabase
    .from('employees')
    .select('id, first_name, last_name, user_id, status, employee_compensation(hourly_rate, overtime_rate)')
    .eq('company_id', companyId);

  if (userRole === 'EMPLOYEE') {
    query = query.eq('user_id', userId);
  }

  query = query.order('first_name', { ascending: true });

  const { data, error } = await query;

  if (error) {
    console.error('Error loading employees:', error);
    return [];
  }

  return (data || []).map(emp => ({
    ...emp,
    full_name: getDisplayName(emp),
    // NOT `|| 35`. That fabricated an hourly rate for anyone whose rate is unset, and
    // unset is the norm rather than the exception: 287 of 300 employees have
    // hourly_rate = 0 (measured live 2026-09-11), so ~96% of people were costed at an
    // invented $35/hr and every labour and profitability figure downstream was built
    // on it. Nothing looked wrong, because $35/hr is a plausible number.
    //
    // The giveaway is the filter directly below: `hourly_rate > 0` was written to
    // exclude unrated employees and could never exclude anyone, because the line above
    // had just forced every value to at least 35. With the real 0 preserved the filter
    // does its job, the weighted average is computed only from rates a human actually
    // entered. (company_settings.labor_rate is the BILLING rate and is not a cost --
    // see costTimesheets in Reports.js, #4350/#4356.)
    hourly_rate: emp.employee_compensation?.hourly_rate ?? 0,
    // NULL stays NULL: an UNSET overtime rate means "1.5x hourly" (resolveOvertimeRate), and a
    // stored 0 means a deliberate $0. Coercing NULL to 0 here costed every unset employee's
    // overtime at $0 on Reports while Labor (raw value) used 1.5x. Live 2026-10-24: 7 rated
    // employees have it unset, none with timesheets yet, so no shown figure was wrong so far.
    overtime_rate: emp.employee_compensation?.overtime_rate ?? null
  }));
};

// Fetch company settings
const fetchReportsLaborSettings = async (companyId) => {
  if (!companyId) return { standard_hours_per_week: 40, labor_rate: { regular: 25, overtime: 37.5 } };
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from('settings')
    .select('labor_rate, overtime_multiplier')
    .eq('company_id', companyId)
    .limit(1);

  if (error || !data || data.length === 0) {
    return { standard_hours_per_week: 40, labor_rate: { regular: 25, overtime: 37.5 } };
  }

  const settings = data[0];
  const laborRate = settings.labor_rate ? parseFloat(settings.labor_rate) : 25;
  const overtimeMultiplier = settings.overtime_multiplier ? parseFloat(settings.overtime_multiplier) : 1.5;
  
  return {
    standard_hours_per_week: 40, // Fixed: no such column in DB, default to 40
    labor_rate: { regular: laborRate, overtime: laborRate * overtimeMultiplier }
  };
};

// Fetch work orders
const fetchWorkOrders = async ({ companyId, dateRange }) => {
  if (!companyId) return [];
  const supabase = getSupabaseClient();

  // Only the columns this page reads (Overview cards + CSV, Revenue Funnel, KPI strip, Jobs /
  // Customers / Documents KPI strips, Days to Invoice). select=* sent ~1 MB for Jerry's year
  // (page speed Phase 2, 2026-10-25); the page text was compared section by section with this
  // list on live data before switching (tests/e2e/speed/list-equivalence.spec.js).
  // A NEW reader of these rows must add its column here.
  const { data: workOrders, error } = await supabase
    .from('work_orders')
    .select('id,status,quote_number,quote_accepted_at,total_amount,created_at,updated_at,scheduled_start,scheduled_end,actual_end,completed_at,rescheduling_requested_at,parent_work_order_id,customer_id')
    .eq('company_id', companyId)
    .gte('created_at', companyDayBounds(dateRange).from)
    .lt('created_at', companyDayBounds(dateRange).to);

  if (error) {
    console.error('Error loading work orders:', error);
    return [];
  }

  // Batch fetch customers
  let customersMap = {};
  if (workOrders && workOrders.length > 0) {
    const customerIds = [...new Set(workOrders.map(wo => wo.customer_id).filter(Boolean))];
    if (customerIds.length > 0) {
      const { data: customers } = await supabase
        .from('customers')
        .select('id, name')
        .in('id', customerIds);
      customersMap = (customers || []).reduce((acc, c) => { acc[c.id] = c; return acc; }, {});
    }
  }

  return (workOrders || []).map(wo => ({
    ...wo,
    customers: customersMap[wo.customer_id] || null
  }));
};

// Fetch invoices
const fetchInvoices = async ({ companyId, dateRange }) => {
  if (!companyId) return [];
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from('invoices')
    // The invoice's own work order comes WITH it. Days to Invoice joins invoices to the
    // separately-fetched workOrders array, but that array is filtered by work_order
    // created_at while invoices are filtered by issue_date — different dates over the same
    // window, so the join misses. Measured live 2026-09-18 on 48f32d34 for the default
    // first-of-month range: 6 invoices in range, 0 of them with a work order created in that
    // range, so every row hit the "no completion date" early return and every bucket showed
    // 0 with an average of 0.0 days (#855).
    //
    // NAMED CONSTRAINT, because there are TWO FKs between these tables: invoices
    // .work_order_id points one way and work_orders.initial_invoice_id points back, so a
    // bare work_orders(...) embed is a PGRST201. check:schema-contract caught this.
    // paid_at feeds the Overview's Invoice Velocity (issue_date -> paid_at); without it every
    // paid invoice was filtered out and the card read 0.0 days (16 qualifying on 48f32d34,
    // avg ~44 days, live 2026-10-11).
    // customer_id: CustomersKPIStrip's Collection Risk reads it, and without it every invoice
    // read undefined and the tile was 0.0% for every company (audit 2026-10-17).
    // payments(payment_date,status): days to pay runs to the payment's date (operator ruling
    // 2026-10-25 C, #6159; utils/invoiceDaysToPay.js). One FK (payments_invoice_id_fkey).
    .select('id,work_order_id,customer_id,status,due_date,issue_date,paid_at,total_amount,balance_due,amount_paid,work_orders!invoices_work_order_id_fkey(actual_end,completed_at,updated_at),payments(payment_date,status)')
    .eq('company_id', companyId)
    .gte('issue_date', dateRange.start)
    .lte('issue_date', `${dateRange.end}T23:59:59`);

  if (error) {
    console.error('Error loading invoices:', error);
    return [];
  }

  return data || [];
};

/**
 * Payments RECEIVED in the range — board #971, operator ruling 2026-09-28: "Reports Cash
 * Collected must use the same cash-basis payments-received formula as the dashboard for the
 * selected period."
 *
 * Deliberately the same shape as the dashboard's fetchCashRevenue
 * (hooks/useAdminDashboardData.js): the payments table, status IN
 * (completed, partially_refunded), filtered on received_at. Not invoices.amount_paid, which
 * is a different question (money against invoices ISSUED in the range) and is what made the
 * two surfaces disagree — $2,037.69 on the dashboard beside $0 here.
 *
 * received_at is a timestamptz, so the end of the range needs the whole day, matching the
 * invoice fetch above.
 */
const fetchPaymentsReceived = async ({ companyId, dateRange }) => {
  if (!companyId) return [];
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from('payments')
    .select('id,amount,refund_amount,status,received_at')
    .eq('company_id', companyId)
    .in('status', ['completed', 'partially_refunded'])
    .gte('received_at', companyDayBounds(dateRange).from)
    .lt('received_at', companyDayBounds(dateRange).to);

  if (error) {
    console.error('Error loading payments:', error);
    return [];
  }

  return data || [];
};

// Fetch expenses
const fetchExpenses = async ({ companyId, dateRange }) => {
  if (!companyId) return [];
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from('expenses')
    .select('*')
    .eq('company_id', companyId)
    .gte('expense_date', dateRange.start)
    .lte('expense_date', dateRange.end);

  if (error) {
    console.error('Error loading expenses:', error);
    return [];
  }

  return data || [];
};

// Fetch timesheets for labor costs
const fetchTimesheetsForLabor = async ({ companyId, dateRange }) => {
  if (!companyId) return [];
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from('employee_timesheets')
    .select('*')
    .eq('company_id', companyId)
    .gte('date', dateRange.start)
    .lte('date', dateRange.end);

  if (error) {
    console.error('Error loading timesheets:', error);
    return [];
  }

  return data || [];
};

// Fetch adjustments summary
const fetchAdjustmentsSummary = async ({ companyId, dateRange }) => {
  if (!companyId) return null;
  try {
    return await AdjustmentsReportingService.getAdjustmentsSummary(
      companyId,
      dateRange.start,
      dateRange.end
    );
  } catch (e) {
    console.warn('Failed to load adjustments analytics', e);
    return null;
  }
};

export const useTimesheetReports = ({ companyId, filters, userRole, userId }) => {
  return useQuery({
    queryKey: ['timesheet-reports', companyId, filters, userRole],
    queryFn: () => fetchTimesheetReports({ companyId, filters, userRole, userId }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false, // refresh map (ruling 1, 2026-11-02): Reports does not refresh automatically
    enabled: !!companyId,
  });
};

export const useEmployees = ({ companyId, userRole, userId }) => {
  return useQuery({
    queryKey: ['employees', companyId, userRole],
    queryFn: () => fetchEmployees({ companyId, userRole, userId }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false, // refresh map (ruling 1, 2026-11-02): Reports does not refresh automatically
    enabled: !!companyId,
  });
};

/**
 * Labor settings for the Reports page — standard hours + labor/overtime rates.
 *
 * RENAMED from `useCompanySettings` 2026-08-21, and the cache key with it. There were
 * TWO exported hooks with this name, doing DIFFERENT things, under the SAME React Query
 * key ['company-settings', companyId]:
 *
 *   useInvoicePageData.useCompanySettings -> settingsService.getCompanyProfile(),
 *       i.e. a `companies` row (SELECT *).
 *   this one                              -> a `settings` row projected to
 *       { standard_hours_per_week, labor_rate: { regular, overtime } }.
 *
 * One key, two shapes: whichever page mounted first populated the cache and the other
 * silently read the wrong object. Reports would find no `labor_rate` on a companies row
 * and keep its defaults; Invoices would hand a labor-settings object to
 * CompletionAndInvoiceModal and the send modal in place of the company profile.
 *
 * Distinct name AND distinct key — renaming only one of them would have left the
 * collision in place.
 */
export const useReportsLaborSettings = (companyId) => {
  return useQuery({
    queryKey: ['reports-labor-settings', companyId],
    queryFn: () => fetchReportsLaborSettings(companyId),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false, // refresh map (ruling 1, 2026-11-02): Reports does not refresh automatically
    enabled: !!companyId,
  });
};

export const useWorkOrdersForReports = ({ companyId, dateRange }) => {
  return useQuery({
    queryKey: ['work-orders-reports', companyId, dateRange],
    queryFn: () => fetchWorkOrders({ companyId, dateRange }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false, // refresh map (ruling 1, 2026-11-02): Reports does not refresh automatically
    enabled: !!companyId,
  });
};

export const useInvoices = ({ companyId, dateRange }) => {
  return useQuery({
    queryKey: ['invoices-reports', companyId, dateRange],
    queryFn: () => fetchInvoices({ companyId, dateRange }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false, // refresh map (ruling 1, 2026-11-02): Reports does not refresh automatically
    enabled: !!companyId,
  });
};

// #5572. A/R aging and Open AR are a POSITION, "as of now" (docs/KPI_DEFINITIONS.md), so they
// cannot come from invoices filtered by issue_date: a range with no new invoices showed $0
// outstanding while $6,388.05 was owed. Every unsettled invoice, whatever its date; callers
// still apply isReceivable() so the one AR rule decides.
const fetchOpenReceivables = async ({ companyId }) => {
  if (!companyId) return [];
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('invoices')
    .select('id,status,due_date,issue_date,total_amount,balance_due,amount_paid')
    .eq('company_id', companyId)
    .not('status', 'in', '(draft,cancelled,void,paid)')
    .gt('balance_due', 0);
  if (error) {
    console.error('Error loading open receivables:', error);
    return [];
  }
  return data || [];
};

export const useOpenReceivables = ({ companyId }) => {
  return useQuery({
    queryKey: ['open-receivables-reports', companyId],
    queryFn: () => fetchOpenReceivables({ companyId }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false, // refresh map (ruling 1, 2026-11-02): Reports does not refresh automatically
    enabled: !!companyId,
  });
};

// Board #971. Cash received in the period, the dashboard's definition.
export const usePaymentsReceived = ({ companyId, dateRange }) => {
  return useQuery({
    queryKey: ['payments-received-reports', companyId, dateRange],
    queryFn: () => fetchPaymentsReceived({ companyId, dateRange }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false, // refresh map (ruling 1, 2026-11-02): Reports does not refresh automatically
    enabled: !!companyId,
  });
};

export const useExpenses = ({ companyId, dateRange }) => {
  return useQuery({
    queryKey: ['expenses-reports', companyId, dateRange],
    queryFn: () => fetchExpenses({ companyId, dateRange }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false, // refresh map (ruling 1, 2026-11-02): Reports does not refresh automatically
    enabled: !!companyId,
  });
};

export const useTimesheetsForLabor = ({ companyId, dateRange }) => {
  return useQuery({
    queryKey: ['timesheets-labor', companyId, dateRange],
    queryFn: () => fetchTimesheetsForLabor({ companyId, dateRange }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false, // refresh map (ruling 1, 2026-11-02): Reports does not refresh automatically
    enabled: !!companyId,
  });
};

// Profitability is on a JOBS-COMPLETED basis (operator ruling 2026-10-19, PRODUCT_DECISIONS).
// A job counts in the period it was FINISHED, and carries ALL of its own costs whenever they
// were logged — time from the day before, materials bought last month. Reading jobs by
// created_at and costs by their own dates (the old basis) put a job's revenue in one month
// and half its costs in another, and counted quotes nobody had done yet.
// Finished date = completed_at, else actual_end / invoiced_at / paid_at / closed_at: 22 of 146
// finished jobs have no completed_at (measured live 2026-10-19), mostly ones invoiced straight
// from a later status, and dropping them would hide real finished work.
export const PROFITABILITY_FINISHED_STATUSES = ['completed', 'invoiced', 'paid', 'closed'];
export const jobFinishedAt = (wo) =>
  wo.completed_at || wo.actual_end || wo.invoiced_at || wo.paid_at || wo.closed_at || null;

// Time actually spent on the job, from its own Actual Start -> Actual End (Start Job / Complete,
// or Edit Job Times / Quick Complete "Save Times"). #6067: Profitability ignored this and costed
// a 4h visit at its 8h estimate. Returns null when the pair is not usable as on-site time:
// missing, under a minute (both stamped by one click — 12 finished jobs live 2026-10-19 have a
// 0.00h span), or over 24h (elapsed calendar days, not hours worked — one job reads 91.8h).
// Crew size is not recorded on the job, so this is one person's time.
export const jobVisitHours = (wo) => {
  if (!wo?.actual_start || !wo?.actual_end) return null;
  const hours = (new Date(wo.actual_end) - new Date(wo.actual_start)) / 3600000;
  if (!Number.isFinite(hours) || hours < 1 / 60 || hours > 24) return null;
  return hours;
};

const fetchProfitabilityJobs = async ({ companyId, dateRange }) => {
  const empty = { workOrders: [], jobExpenses: [], jobTimesheets: [], jobLineItems: [] };
  if (!companyId) return empty;
  const supabase = getSupabaseClient();
  const { from, to } = companyDayBounds(dateRange);

  // Only the columns Profitability reads (jobFinishedAt, jobVisitHours, calculateProfitability
  // in Reports.js). Same equivalence check as fetchWorkOrders; a new reader adds its column here.
  const { data: rows, error } = await supabase
    .from('work_orders')
    .select('id,status,title,work_order_number,quote_number,customer_id,total_amount,estimated_duration_hours,estimated_duration,scheduled_start,scheduled_end,actual_start,actual_end,completed_at,invoiced_at,paid_at,closed_at')
    .eq('company_id', companyId)
    .in('status', PROFITABILITY_FINISHED_STATUSES);
  if (error) {
    console.error('Error loading completed jobs:', error);
    return empty;
  }
  const fromMs = new Date(from).getTime();
  const toMs = new Date(to).getTime();
  const jobs = (rows || []).filter((wo) => {
    const at = jobFinishedAt(wo);
    if (!at) return false;
    const t = new Date(at).getTime();
    return t >= fromMs && t < toMs;
  });
  if (jobs.length === 0) return empty;

  const ids = jobs.map((wo) => wo.id);
  const customerIds = [...new Set(jobs.map((wo) => wo.customer_id).filter(Boolean))];
  const [customersRes, expensesRes, timesheetsRes, lineItemsRes] = await Promise.all([
    customerIds.length
      ? supabase.from('customers').select('id, name').in('id', customerIds)
      : Promise.resolve({ data: [] }),
    supabase.from('expenses').select('*').eq('company_id', companyId).in('work_order_id', ids),
    supabase.from('employee_timesheets').select('*').eq('company_id', companyId).in('work_order_id', ids),
    // Ruling 2026-11-02: the job's own line costs (material cost) and hourly service lines
    // (planned labour). Work order lines, not invoice lines: revenue here is
    // work_orders.total_amount, which the DB trigger totals from these same lines, and
    // invoice_line_items has no cost column.
    supabase.from('work_order_line_items')
      .select('work_order_id,line_type,quantity,cost,unit_label,is_optional,optional_selected')
      .eq('company_id', companyId).in('work_order_id', ids),
  ]);
  if (expensesRes.error) console.error('Error loading job expenses:', expensesRes.error);
  if (timesheetsRes.error) console.error('Error loading job time:', timesheetsRes.error);
  if (lineItemsRes.error) console.error('Error loading job line items:', lineItemsRes.error);
  const customersMap = (customersRes.data || []).reduce((acc, c) => { acc[c.id] = c; return acc; }, {});

  return {
    workOrders: jobs.map((wo) => ({ ...wo, customers: customersMap[wo.customer_id] || null })),
    jobExpenses: expensesRes.data || [],
    jobTimesheets: timesheetsRes.data || [],
    jobLineItems: lineItemsRes.data || [],
  };
};

export const useProfitabilityJobs = ({ companyId, dateRange }) => {
  return useQuery({
    queryKey: ['profitability-jobs', companyId, dateRange],
    queryFn: () => fetchProfitabilityJobs({ companyId, dateRange }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false, // refresh map (ruling 1, 2026-11-02): Reports does not refresh automatically
    enabled: !!companyId,
  });
};

export const useAdjustmentsSummary = ({ companyId, dateRange }) => {
  return useQuery({
    queryKey: ['adjustments-summary', companyId, dateRange],
    queryFn: () => fetchAdjustmentsSummary({ companyId, dateRange }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false, // refresh map (ruling 1, 2026-11-02): Reports does not refresh automatically
    enabled: !!companyId,
  });
};
